/**
 * Yard Map View — core map editor component.
 *
 * Renders the interactive SVG canvas with sections, a section detail panel
 * for viewing assets and assigning new ones, and the SectionSetupWizard
 * for creating new sections. Extracted from YardMapPage so it can be
 * reused in both the Inventory Hub tab and the Settings overlay.
 */

import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  Plus,
  Trash2,
  Move,
  Grid3X3,
  Search,
  AlertTriangle,
  EyeOff,
  Ruler,
  Spline,
  PenTool,
  PackageOpen,
  MapPin,
  LayoutTemplate,
  ArrowRight,
  MousePointerClick,
  X,
  Building2,
  Layers,
  ChevronDown,
  Undo2,
  Redo2,
  RotateCw,
  Copy,
  HelpCircle,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import {
  useLocationSchemes,
  useStorageAreas,
  usePlaceArea,
  useCreateStorageArea,
  useCreateFeature,
  useUpdateFeature,
  useDeleteFeature,
  type StorageArea,
} from "@/features/yards/api";
import YardCanvas, { AREA_DRAG_MIME } from "@/features/yards/YardCanvas";
import AssetPicker from "@/features/yards/AssetPicker";
import SectionSetupWizard from "@/features/yards/SectionSetupWizard";
import ShortcutsOverlay from "@/features/yards/ShortcutsOverlay";
import CanvasContextMenu, {
  type ContextMenuTarget,
} from "@/features/yards/CanvasContextMenu";
import { useEditorHistory } from "@/features/yards/useEditorHistory";
import {
  getBoundingBox,
  getShapeVertices,
  segmentLength,
  movePointAlongSegment,
  offsetPolygon,
  shapeClearance,
  polygonsOverlap,
  polygonWithin,
  nearestPointOnPolygon,
  distancePointToPolygon,
  polygonArea,
  polygonPerimeter,
  type Point,
  type ShapeType,
} from "@/features/yards/shapeUtils";

/**
 * Gap between a contained shape and the boundary it sits inside. Unlike
 * `shapeClearance` (which returns 0 when one polygon contains another), this
 * measures edge-to-edge distance, so an area *inside* the boundary reports its
 * true clearance. Returns 0 only if the shape actually crosses the boundary.
 */
function gapToBoundary(inner: Point[], boundary: Point[]): number {
  if (!polygonWithin(inner, boundary)) return 0;
  let min = Infinity;
  for (const p of inner)
    min = Math.min(min, distancePointToPolygon(p, boundary));
  return min;
}
import ActionDrawer from "@/components/ui/ActionDrawer";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import type { YardMapData, YardFeature, FeatureKind } from "@/types/yard";

/**
 * A pen-tool drawing session — what the next completed shape becomes: the
 * facility boundary, a storage area (new/reshaped), or a reference feature
 * (new/reshaped). Features carry their kind + geometry so the pen tool knows
 * whether to close (polygon) or stay open (polyline, e.g. a fence).
 */
type DrawSession =
  | { kind: "boundary" }
  | { kind: "area-new" }
  | { kind: "area-reshape"; sectionId: string }
  | {
      kind: "feature-new";
      featureKind: FeatureKind;
      geometry: "polygon" | "polyline";
    }
  | {
      kind: "feature-reshape";
      featureId: string;
      geometry: "polygon" | "polyline";
    };

/** Geometry each feature kind is traced with. */
const FEATURE_GEOMETRY: Record<FeatureKind, "polygon" | "polyline"> = {
  building: "polygon",
  driveway: "polygon",
  landmark: "polygon",
  other: "polygon",
  fence: "polyline",
  gate: "polyline",
};

/** Feature kinds offered in the "Add feature" menu, with display labels. */
const FEATURE_MENU: Array<{ kind: FeatureKind; label: string }> = [
  { kind: "building", label: "Building" },
  { kind: "fence", label: "Fence" },
  { kind: "driveway", label: "Driveway" },
  { kind: "gate", label: "Gate" },
  { kind: "landmark", label: "Landmark" },
  { kind: "other", label: "Other" },
];

/** Canvas extent (max x/y, rounded up) that fully contains a polygon. */
function deriveExtent(points: Point[]): { width: number; height: number } {
  const bbox = getBoundingBox(points);
  return {
    width: Math.max(1, Math.ceil(bbox.x + bbox.width)),
    height: Math.max(1, Math.ceil(bbox.y + bbox.height)),
  };
}

/** Axis-aligned rectangle boundary from width/height (origin at 0,0). */
function rectBoundary(width: number, height: number): Point[] {
  return [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ];
}

/** A section as it is actually rendered — the polygon used for clearance math. */
function sectionPolygon(s: {
  shape_type?: string | null;
  points?: Point[] | null;
  x: number;
  y: number;
  width: number;
  height: number;
}): Point[] {
  const shape = (s.shape_type ?? "rectangle") as ShapeType;
  if (shape === "polygon" && s.points && s.points.length >= 3) {
    // Stored absolute; SectionShape translates by (x - minX) as it is dragged.
    const minX = Math.min(...s.points.map((p) => p.x));
    const minY = Math.min(...s.points.map((p) => p.y));
    const dx = s.x - minX;
    const dy = s.y - minY;
    return s.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
  }
  return getShapeVertices(shape, s.x, s.y, s.width, s.height);
}

/** Error boundary to catch render crashes and show a useful message instead of a white screen. */
export class YardMapErrorBoundary extends React.Component<
  { children: React.ReactNode; onReset?: () => void },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode; onReset?: () => void }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("YardMapView crash:", error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center py-12 text-center">
          <AlertTriangle className="h-10 w-10 text-[var(--text-danger)]" />
          <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
            Something went wrong
          </h3>
          <p className="mt-2 text-sm text-[var(--text-secondary)] max-w-md">
            {this.state.error.message}
          </p>
          <pre className="mt-3 text-xs text-[var(--text-tertiary)] max-w-lg overflow-auto bg-[var(--surface-sunken)] p-3 rounded-lg">
            {this.state.error.stack?.split("\n").slice(0, 5).join("\n")}
          </pre>
          <button
            className="mt-4 text-sm text-brand-600 hover:underline"
            onClick={() => {
              this.setState({ error: null });
              this.props.onReset?.();
            }}
          >
            Go back
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Utilization summary bar for a yard. */
export function YardUtilizationBar({ yardId }: { yardId: string }) {
  const { data: mapData } = useQuery<YardMapData>({
    queryKey: ["yard-map", yardId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/yards/${yardId}/map`)).data as YardMapData,
  });

  if (!mapData) return null;

  const sections = mapData.sections || [];
  const totalCapacity = sections.reduce((sum, s) => sum + (s.capacity || 0), 0);
  const totalAssets = sections.reduce(
    (sum, s) => sum + (s.assets?.length || 0),
    0,
  );
  const utilPct =
    totalCapacity > 0 ? Math.round((totalAssets / totalCapacity) * 100) : 0;

  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-base)] px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Sections
        </p>
        <p className="text-lg font-bold text-[var(--text-primary)] tabular-nums">
          {sections.length}
        </p>
      </div>
      <div className="rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-base)] px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Assets Placed
        </p>
        <p className="text-lg font-bold text-[var(--text-primary)] tabular-nums">
          {totalAssets}
          {totalCapacity > 0 && (
            <span className="text-xs font-normal text-[var(--text-tertiary)] ml-1">
              / {totalCapacity}
            </span>
          )}
        </p>
      </div>
      <div className="rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-base)] px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Utilization
        </p>
        <div className="flex items-center gap-2">
          <p className="text-lg font-bold text-[var(--text-primary)] tabular-nums">
            {utilPct}%
          </p>
          <div className="flex-1 h-2 rounded-full bg-[var(--surface-sunken)]">
            <div
              className={`h-2 rounded-full ${utilPct > 80 ? "bg-[var(--color-danger)]" : utilPct > 50 ? "bg-[var(--color-warning)]" : "bg-[var(--color-success)]"}`}
              style={{ width: `${Math.min(utilPct, 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Interactive yard map editor with canvas and section detail panel. */
export default function YardMapView({ yardId }: { yardId: string }) {
  const qc = useQueryClient();

  const {
    data: mapData,
    isLoading,
    error: mapError,
  } = useQuery<YardMapData>({
    queryKey: ["yard-map", yardId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/yards/${yardId}/map`)).data as YardMapData,
  });

  const { data: schemes } = useLocationSchemes();

  const [showAddSection, setShowAddSection] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  const [assignLocationId, setAssignLocationId] = useState("");
  const [locationSearch, setLocationSearch] = useState("");

  // Two-step workflow: "boundary" = define/edit the facility outline;
  // "layout" = place storage areas & features inside the locked boundary.
  const [editorMode, setEditorMode] = useState<"boundary" | "layout">("layout");

  // Active pen-tool session (null = not drawing). See DrawSession.
  const [draw, setDraw] = useState<DrawSession | null>(null);

  // A freshly drawn area outline awaiting classification (assign/create).
  const [pendingShape, setPendingShape] = useState<Point[] | null>(null);

  // Selected reference feature (mutually exclusive with a selected section).
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  // Feature kinds hidden via the layer toggles.
  const [hiddenKinds, setHiddenKinds] = useState<Set<FeatureKind>>(new Set());
  // "Add feature" kind menu open state.
  const [featureMenuOpen, setFeatureMenuOpen] = useState(false);

  // Editing: undo/redo history, session locks, context menu, shortcuts help.
  const history = useEditorHistory();
  const [lockedIds, setLockedIds] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<ContextMenuTarget | null>(
    null,
  );
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Map-settings drawer (dimensions/unit) + quick-rectangle setup dialog.
  const [showMapSettings, setShowMapSettings] = useState(false);
  const [showQuickDims, setShowQuickDims] = useState(false);

  // Fit-to-container scaling: measure the canvas column so real-world
  // dimensions (e.g. 250 ft) scale up to fill the available width.
  const canvasColRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = canvasColRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const deleteSectionMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      await apiClient.delete(`/api/v1/yards/${yardId}/sections/${sectionId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["yard-map", yardId] });
      setSelectedSection(null);
      toast.success("Section deleted");
    },
    onError: () => toast.error("Failed to delete section"),
  });

  const unplaceMutation = useMutation({
    mutationFn: async (sectionId: string) =>
      (await apiClient.post(`/api/v1/storage-areas/${sectionId}/unplace`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["yard-map", yardId] });
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
      setSelectedSection(null);
      toast.success(
        "Storage area removed from map (area and locations preserved)",
      );
    },
    onError: () => toast.error("Failed to unplace storage area"),
  });

  const unassignMutation = useMutation({
    mutationFn: async (assetId: string) =>
      (
        await apiClient.post(`/api/v1/yards/assign-asset`, {
          asset_id: assetId,
          yard_section_id: null,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["yard-map", yardId] });
      toast.success("Asset removed from section");
    },
    onError: () => toast.error("Failed to unassign asset"),
  });

  // Update yard-level properties (dimensions, unit, custom boundary).
  const updateYardMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.put(`/api/v1/yards/${yardId}`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yard-map", yardId] }),
    onError: () => toast.error("Failed to update facility map"),
  });

  // Persist a section's custom polygon (shape + bounding box).
  const updateSectionShapeMutation = useMutation({
    mutationFn: async ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: Record<string, unknown>;
    }) =>
      (
        await apiClient.put(
          `/api/v1/yards/${yardId}/sections/${sectionId}`,
          data,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yard-map", yardId] }),
    onError: () => toast.error("Failed to update storage area shape"),
  });

  const placeAreaMutation = usePlaceArea();
  const createAreaMutation = useCreateStorageArea();
  const createFeatureMutation = useCreateFeature(yardId);
  const updateFeatureMutation = useUpdateFeature(yardId);
  const deleteFeatureMutation = useDeleteFeature(yardId);

  const sections = useMemo(() => mapData?.sections ?? [], [mapData?.sections]);
  const yard = mapData?.yard;
  const totalAssets = sections.reduce(
    (sum, s) => sum + (s.asset_count || 0),
    0,
  );
  const selectedData = sections.find((s) => s.id === selectedSection);
  const selectedAssets = selectedData?.assets ?? [];
  const selectedLocations = useMemo(
    () => selectedData?.locations ?? [],
    [selectedData?.locations],
  );
  const hasScheme = !!selectedData?.location_scheme_id;

  const unit = yard?.unit ?? "ft";
  const boundaryPoints = (yard?.boundary_points as Point[] | null) ?? null;
  const hasBoundary = !!(boundaryPoints && boundaryPoints.length >= 3);
  const businessLocationId = yard?.business_location_id ?? null;

  // A facility with no boundary yet is locked into the boundary-setup step;
  // otherwise the user's chosen step applies. Derived to avoid load flicker.
  const mode: "boundary" | "layout" = hasBoundary ? editorMode : "boundary";

  // Fit-to-container scale: fill the available width, clamped so tiny or huge
  // maps stay legible. Falls back to a window estimate before first measure.
  const scale = useMemo(() => {
    if (!yard) return 1;
    const gutter = 20; // ruler gutter (map units)
    const available = containerWidth || window.innerWidth - 400;
    const raw = available / (yard.width + gutter);
    return Math.max(0.05, Math.min(12, raw));
  }, [yard, containerWidth]);

  // Unplaced storage areas for this facility, offered as a drag palette.
  const { data: storageAreas } = useStorageAreas(businessLocationId);
  const unplacedAreas = useMemo(
    () => (storageAreas ?? []).filter((a) => !a.is_placed),
    [storageAreas],
  );

  // Reference features (buildings/fences/...) and the layer set present.
  const features = useMemo<YardFeature[]>(
    () => mapData?.features ?? [],
    [mapData?.features],
  );
  const featureKindsPresent = useMemo(
    () => Array.from(new Set(features.map((f) => f.kind))),
    [features],
  );
  const visibleFeatures = useMemo(
    () => features.filter((f) => !hiddenKinds.has(f.kind)),
    [features, hiddenKinds],
  );
  const selectedFeatureData = features.find((f) => f.id === selectedFeature);

  // Selecting a feature clears any selected section and vice-versa.
  const selectSection = useCallback((id: string | null) => {
    setSelectedSection(id);
    if (id) setSelectedFeature(null);
  }, []);
  const selectFeature = useCallback((id: string | null) => {
    setSelectedFeature(id);
    if (id) setSelectedSection(null);
  }, []);

  const toggleKind = useCallback((kind: FeatureKind) => {
    setHiddenKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }, []);

  // --- Editing: history, geometry commit, rotate, duplicate, nudge, lock -----

  const putSection = useCallback(
    (id: string, data: Record<string, unknown>) =>
      apiClient
        .put(`/api/v1/yards/${yardId}/sections/${id}`, data)
        .then(() => qc.invalidateQueries({ queryKey: ["yard-map", yardId] })),
    [yardId, qc],
  );
  const putFeature = useCallback(
    (id: string, data: Record<string, unknown>) =>
      apiClient
        .put(`/api/v1/yards/${yardId}/features/${id}`, data)
        .then(() => qc.invalidateQueries({ queryKey: ["yard-map", yardId] })),
    [yardId, qc],
  );

  /** Persist a section geometry/property change and record it for undo. */
  const commitSection = useCallback(
    (
      id: string,
      before: Record<string, unknown>,
      after: Record<string, unknown>,
    ) => {
      void putSection(id, after);
      history.push({
        undo: () => putSection(id, before),
        redo: () => putSection(id, after),
      });
    },
    [putSection, history],
  );
  const commitFeature = useCallback(
    (
      id: string,
      before: Record<string, unknown>,
      after: Record<string, unknown>,
    ) => {
      void putFeature(id, after);
      history.push({
        undo: () => putFeature(id, before),
        redo: () => putFeature(id, after),
      });
    },
    [putFeature, history],
  );

  // Drag/resize from the canvas commit through here (persist + history).
  const commitSectionGeometry = useCallback(
    (
      id: string,
      before: Record<string, number>,
      after: Record<string, number>,
    ) => {
      commitSection(id, before, after);
    },
    [commitSection],
  );

  const rotateSection = useCallback(
    (id: string, deg: number) => {
      const s = sections.find((x) => x.id === id);
      if (!s) return;
      const cur = s.rotation ?? 0;
      commitSection(
        id,
        { rotation: cur },
        { rotation: (cur + deg + 360) % 360 },
      );
    },
    [sections, commitSection],
  );

  const duplicateSection = useCallback(
    (id: string) => {
      const s = sections.find((x) => x.id === id);
      if (!s || !businessLocationId) {
        toast.error("Cannot duplicate here");
        return;
      }
      const off = 20;
      const shape = (s.shape_type ?? "rectangle") as string;
      const pts =
        shape === "polygon" && s.points
          ? s.points.map((p) => ({ x: p.x + off, y: p.y + off }))
          : undefined;
      createAreaMutation.mutate(
        {
          label: `${s.label} copy`,
          section_type: s.section_type,
          business_location_id: businessLocationId,
        },
        {
          onSuccess: (area) => {
            placeAreaMutation.mutate(
              {
                areaId: area.id,
                yard_id: yardId,
                x: Math.round(s.x + off),
                y: Math.round(s.y + off),
                width: Math.round(s.width),
                height: Math.round(s.height),
                shape_type: shape,
                points: pts,
                rotation: s.rotation ?? 0,
              },
              {
                onSuccess: () => {
                  toast.success("Storage area duplicated");
                  setSelectedSection(area.id);
                },
              },
            );
          },
          onError: () => toast.error("Failed to duplicate"),
        },
      );
    },
    [
      sections,
      businessLocationId,
      yardId,
      createAreaMutation,
      placeAreaMutation,
    ],
  );

  const duplicateFeature = useCallback(
    (id: string) => {
      const f = features.find((x) => x.id === id);
      if (!f) return;
      const off = 20;
      createFeatureMutation.mutate(
        {
          label: `${f.label} copy`,
          kind: f.kind,
          geometry_type: f.geometry_type,
          points: f.points.map((p) => ({ x: p.x + off, y: p.y + off })),
        },
        {
          onSuccess: () => toast.success("Feature duplicated"),
          onError: () => toast.error("Failed to duplicate"),
        },
      );
    },
    [features, createFeatureMutation],
  );

  /** Move a section by (dx, dy), persisting + recording history. */
  const moveSectionBy = useCallback(
    (s: (typeof sections)[number], dx: number, dy: number) => {
      if ((s.shape_type ?? "rectangle") === "polygon" && s.points) {
        const pts = s.points.map((p) => ({
          x: Math.round(p.x + dx),
          y: Math.round(p.y + dy),
        }));
        const bbox = getBoundingBox(pts);
        commitSection(
          s.id,
          {
            points: s.points,
            x: Math.round(s.x),
            y: Math.round(s.y),
            width: Math.round(s.width),
            height: Math.round(s.height),
          },
          {
            points: pts,
            x: Math.round(bbox.x),
            y: Math.round(bbox.y),
            width: Math.round(bbox.width),
            height: Math.round(bbox.height),
          },
        );
      } else {
        commitSection(
          s.id,
          { x: Math.round(s.x), y: Math.round(s.y) },
          { x: Math.round(s.x + dx), y: Math.round(s.y + dy) },
        );
      }
    },
    [commitSection],
  );

  const nudgeSelection = useCallback(
    (dx: number, dy: number) => {
      if (selectedData && !lockedIds.has(selectedData.id)) {
        moveSectionBy(selectedData, dx, dy);
      } else if (selectedFeatureData) {
        const pts = selectedFeatureData.points.map((p) => ({
          x: p.x + dx,
          y: p.y + dy,
        }));
        commitFeature(
          selectedFeatureData.id,
          { points: selectedFeatureData.points },
          { points: pts },
        );
      }
    },
    [
      selectedData,
      selectedFeatureData,
      lockedIds,
      moveSectionBy,
      commitFeature,
    ],
  );

  const toggleLock = useCallback((id: string) => {
    setLockedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const deleteSelected = useCallback(() => {
    if (selectedData) {
      if (lockedIds.has(selectedData.id)) return;
      const n = selectedData.asset_count || 0;
      if (
        confirm(
          `Delete "${selectedData.label}"?${n > 0 ? ` ${n} asset(s) will be un-assigned.` : ""}`,
        )
      ) {
        deleteSectionMutation.mutate(selectedData.id);
      }
    } else if (selectedFeatureData) {
      deleteFeatureMutation.mutate(selectedFeatureData.id, {
        onSuccess: () => {
          setSelectedFeature(null);
          toast.success("Feature deleted");
        },
      });
    }
  }, [
    selectedData,
    selectedFeatureData,
    lockedIds,
    deleteSectionMutation,
    deleteFeatureMutation,
  ]);

  // Global editor keyboard shortcuts (undo/redo, duplicate, delete, nudge, ?).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const mod = e.metaKey || e.ctrlKey;
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) {
        setShowShortcuts((v) => !v);
        return;
      }
      if (mod && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) history.redo();
        else history.undo();
        return;
      }
      if (mod && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        history.redo();
        return;
      }
      if (draw) return; // drawing owns its own keys
      if (mod && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        if (selectedData) duplicateSection(selectedData.id);
        else if (selectedFeatureData) duplicateFeature(selectedFeatureData.id);
        return;
      }
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        (selectedData || selectedFeatureData)
      ) {
        e.preventDefault();
        deleteSelected();
        return;
      }
      const step = e.shiftKey ? 10 : 1;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        nudgeSelection(-step, 0);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        nudgeSelection(step, 0);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        nudgeSelection(0, -step);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        nudgeSelection(0, step);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    history,
    draw,
    selectedData,
    selectedFeatureData,
    duplicateSection,
    duplicateFeature,
    deleteSelected,
    nudgeSelection,
  ]);

  // --- Clearance & setbacks -------------------------------------------------

  const setback = yard?.setback ?? null;
  const boundaryPoly = hasBoundary ? boundaryPoints : null;
  const setbackPoints = useMemo(
    () =>
      boundaryPoly && setback ? offsetPolygon(boundaryPoly, setback) : null,
    [boundaryPoly, setback],
  );

  // Closed shapes (storage areas + closed features) for overlap/clearance.
  const closedShapes = useMemo(() => {
    const secs = sections.map((s) => ({
      id: s.id,
      label: s.label,
      poly: sectionPolygon(s),
    }));
    const feats = features
      .filter((f) => f.geometry_type === "polygon" && f.points.length >= 3)
      .map((f) => ({ id: f.id, label: f.label, poly: f.points as Point[] }));
    return [...secs, ...feats];
  }, [sections, features]);

  // Clearance detail for the currently selected storage area.
  const selectedClearance = useMemo(() => {
    if (!selectedData) return null;
    const poly = sectionPolygon(selectedData);
    let nearest: { label: string; gap: number } | null = null;
    let overlapsWith: string | null = null;
    for (const o of closedShapes) {
      if (o.id === selectedData.id) continue;
      if (polygonsOverlap(poly, o.poly)) {
        overlapsWith = o.label;
        break;
      }
      const gap = shapeClearance(poly, o.poly);
      if (!nearest || gap < nearest.gap) nearest = { label: o.label, gap };
    }
    return {
      boundaryGap: boundaryPoly ? gapToBoundary(poly, boundaryPoly) : null,
      crossesBoundary: boundaryPoly
        ? !polygonWithin(poly, boundaryPoly)
        : false,
      crossesSetback: setbackPoints
        ? !polygonWithin(poly, setbackPoints)
        : false,
      nearest,
      overlapsWith,
    };
  }, [selectedData, closedShapes, boundaryPoly, setbackPoints]);

  // Facility-wide violations for the warning banner.
  const violations = useMemo(() => {
    const list: Array<{ id: string; label: string; issue: string }> = [];
    for (const s of sections) {
      const poly = sectionPolygon(s);
      if (boundaryPoly && !polygonWithin(poly, boundaryPoly)) {
        list.push({ id: s.id, label: s.label, issue: "crosses the boundary" });
      } else if (setbackPoints && !polygonWithin(poly, setbackPoints)) {
        list.push({ id: s.id, label: s.label, issue: "enters the setback" });
      }
      for (const o of closedShapes) {
        if (o.id !== s.id && polygonsOverlap(poly, o.poly)) {
          list.push({ id: s.id, label: s.label, issue: `overlaps ${o.label}` });
          break;
        }
      }
    }
    return list;
  }, [sections, boundaryPoly, setbackPoints, closedShapes]);

  // Move a section by (dx, dy) and persist (polygon vs box aware).
  const applyNudge = useCallback(
    (s: (typeof sections)[number], dx: number, dy: number) => {
      if ((s.shape_type ?? "rectangle") === "polygon" && s.points) {
        const pts = s.points.map((p) => ({
          x: Math.round(p.x + dx),
          y: Math.round(p.y + dy),
        }));
        const bbox = getBoundingBox(pts);
        updateSectionShapeMutation.mutate({
          sectionId: s.id,
          data: {
            points: pts,
            x: Math.round(bbox.x),
            y: Math.round(bbox.y),
            width: Math.round(bbox.width),
            height: Math.round(bbox.height),
          },
        });
      } else {
        updateSectionShapeMutation.mutate({
          sectionId: s.id,
          data: { x: Math.round(s.x + dx), y: Math.round(s.y + dy) },
        });
      }
    },
    [updateSectionShapeMutation],
  );

  // Nudge the selected area so its gap to the boundary equals `target`.
  const nudgeSelectedToBoundary = useCallback(
    (target: number) => {
      if (!selectedData || !boundaryPoly) return;
      if (!Number.isFinite(target) || target < 0) return;
      const poly = sectionPolygon(selectedData);
      let av = poly[0]!;
      let bp = poly[0]!;
      let bestD = Infinity;
      for (const p of poly) {
        const np = nearestPointOnPolygon(p, boundaryPoly);
        if (np.dist < bestD) {
          bestD = np.dist;
          av = p;
          bp = np.point;
        }
      }
      if (bestD < 1e-6) {
        toast.error("Move the area off the boundary first");
        return;
      }
      const dirx = (av.x - bp.x) / bestD;
      const diry = (av.y - bp.y) / bestD;
      const move = target - bestD;
      applyNudge(selectedData, dirx * move, diry * move);
      toast.success(`Set ${target} ${unit} from boundary`);
    },
    [selectedData, boundaryPoly, applyNudge, unit],
  );

  // Persist a boundary polygon and re-derive the canvas extent so rulers and
  // scale match the traced outline.
  const saveBoundary = useCallback(
    (points: Point[]) => {
      const { width, height } = deriveExtent(points);
      updateYardMutation.mutate(
        { boundary_points: points, width, height },
        {
          onSuccess: () => {
            toast.success("Facility boundary saved");
            setEditorMode("layout");
          },
        },
      );
    },
    [updateYardMutation],
  );

  // Resolve a completed pen-tool shape per the active draw session.
  const handleDrawComplete = useCallback(
    (points: Point[]) => {
      const session = draw;
      setDraw(null);
      if (!session) return;
      const isPolyline =
        (session.kind === "feature-new" ||
          session.kind === "feature-reshape") &&
        session.geometry === "polyline";
      if (points.length < (isPolyline ? 2 : 3)) return;

      if (session.kind === "boundary") {
        saveBoundary(points);
      } else if (session.kind === "area-new") {
        // Hand off to the classification dialog (assign existing / create new).
        setPendingShape(points);
      } else if (session.kind === "area-reshape") {
        const bbox = getBoundingBox(points);
        updateSectionShapeMutation.mutate(
          {
            sectionId: session.sectionId,
            data: {
              shape_type: "polygon",
              points,
              x: Math.round(bbox.x),
              y: Math.round(bbox.y),
              width: Math.round(bbox.width),
              height: Math.round(bbox.height),
            },
          },
          { onSuccess: () => toast.success("Shape saved") },
        );
      } else if (session.kind === "feature-new") {
        const count = features.filter(
          (f) => f.kind === session.featureKind,
        ).length;
        const label = `${session.featureKind[0]!.toUpperCase()}${session.featureKind.slice(1)} ${count + 1}`;
        createFeatureMutation.mutate(
          {
            label,
            kind: session.featureKind,
            geometry_type: session.geometry,
            points,
          },
          {
            onSuccess: () => toast.success(`${label} added`),
            onError: () => toast.error("Failed to add feature"),
          },
        );
      } else if (session.kind === "feature-reshape") {
        updateFeatureMutation.mutate(
          { featureId: session.featureId, data: { points } },
          {
            onSuccess: () => toast.success("Feature updated"),
            onError: () => toast.error("Failed to update feature"),
          },
        );
      }
    },
    [
      draw,
      saveBoundary,
      updateSectionShapeMutation,
      features,
      createFeatureMutation,
      updateFeatureMutation,
    ],
  );

  // Drop an unplaced storage area onto the canvas at the cursor.
  const handleDropArea = useCallback(
    (areaId: string, x: number, y: number) => {
      if (!yard) return;
      const w = 80;
      const h = 60;
      placeAreaMutation.mutate(
        {
          areaId,
          yard_id: yardId,
          x: Math.max(0, Math.round(x - w / 2)),
          y: Math.max(0, Math.round(y - h / 2)),
          width: w,
          height: h,
        },
        {
          onSuccess: () => toast.success("Storage area placed"),
          onError: () => toast.error("Failed to place storage area"),
        },
      );
    },
    [yard, yardId, placeAreaMutation],
  );

  // Place the pending drawn outline onto an existing unplaced storage area.
  const classifyExisting = useCallback(
    (areaId: string) => {
      if (!pendingShape) return;
      const bbox = getBoundingBox(pendingShape);
      placeAreaMutation.mutate(
        {
          areaId,
          yard_id: yardId,
          x: Math.round(bbox.x),
          y: Math.round(bbox.y),
          width: Math.round(bbox.width),
          height: Math.round(bbox.height),
          shape_type: "polygon",
          points: pendingShape,
        },
        {
          onSuccess: (_d) => {
            toast.success("Storage area placed");
            setPendingShape(null);
            setSelectedSection(areaId);
          },
          onError: () => toast.error("Failed to place storage area"),
        },
      );
    },
    [pendingShape, yardId, placeAreaMutation],
  );

  // Create a new storage area from the pending outline, then place it.
  const classifyNew = useCallback(
    (label: string, sectionType: string) => {
      if (!pendingShape || !businessLocationId) return;
      const shape = pendingShape;
      const bbox = getBoundingBox(shape);
      createAreaMutation.mutate(
        {
          label,
          section_type: sectionType,
          business_location_id: businessLocationId,
        },
        {
          onSuccess: (area) => {
            placeAreaMutation.mutate(
              {
                areaId: area.id,
                yard_id: yardId,
                x: Math.round(bbox.x),
                y: Math.round(bbox.y),
                width: Math.round(bbox.width),
                height: Math.round(bbox.height),
                shape_type: "polygon",
                points: shape,
              },
              {
                onSuccess: () => {
                  toast.success("Storage area created");
                  setPendingShape(null);
                  setSelectedSection(area.id);
                },
                onError: () => toast.error("Failed to place storage area"),
              },
            );
          },
          onError: () => toast.error("Failed to create storage area"),
        },
      );
    },
    [
      pendingShape,
      businessLocationId,
      yardId,
      createAreaMutation,
      placeAreaMutation,
    ],
  );

  // Edit one boundary segment to an exact length (moves its end vertex).
  const handleSegmentLength = useCallback(
    (index: number, newLength: number) => {
      if (!boundaryPoints || boundaryPoints.length < 3) return;
      if (!Number.isFinite(newLength) || newLength <= 0) return;
      const anchor = boundaryPoints[index]!;
      const endIdx = (index + 1) % boundaryPoints.length;
      const moved = movePointAlongSegment(
        anchor,
        boundaryPoints[endIdx]!,
        newLength,
      );
      const next = boundaryPoints.map((p, i) =>
        i === endIdx ? { x: Math.round(moved.x), y: Math.round(moved.y) } : p,
      );
      updateYardMutation.mutate({ boundary_points: next });
    },
    [boundaryPoints, updateYardMutation],
  );

  const filteredLocations = useMemo(() => {
    if (selectedLocations.length === 0) return [];
    if (!locationSearch) return selectedLocations;
    const q = locationSearch.toLowerCase();
    return selectedLocations.filter((l) => l.label.toLowerCase().includes(q));
  }, [selectedLocations, locationSearch]);

  if (isLoading) return <LoadingSpinner className="py-12" />;

  if (mapError || !yard) {
    return (
      <div className="space-y-4">
        <Card>
          <div className="flex flex-col items-center py-12 text-center">
            <AlertTriangle className="h-10 w-10 text-[var(--text-danger)]" />
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              Failed to load facility map
            </h3>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              {mapError instanceof Error
                ? mapError.message
                : "The facility map data could not be loaded. Please try again."}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-primary)]">
            {yard.name}
          </h2>
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
            {sections.length} storage area{sections.length !== 1 ? "s" : ""} ·{" "}
            {totalAssets} assets placed
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Undo / redo / help (always available) */}
          <div className="flex items-center gap-0.5 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-1 py-0.5">
            <button
              type="button"
              title="Undo (⌘Z)"
              onClick={history.undo}
              disabled={!history.canUndo}
              className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] disabled:opacity-40"
            >
              <Undo2 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Redo (⌘⇧Z)"
              onClick={history.redo}
              disabled={!history.canRedo}
              className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] disabled:opacity-40"
            >
              <Redo2 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Keyboard shortcuts (?)"
              onClick={() => setShowShortcuts(true)}
              className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </button>
          </div>
          {hasBoundary && <ModeStepper mode={mode} onChange={setEditorMode} />}
          <div className="flex gap-2">
            {mode === "boundary" ? (
              <>
                <Button
                  size="sm"
                  variant={draw?.kind === "boundary" ? "primary" : "secondary"}
                  onClick={() =>
                    setDraw((d) =>
                      d?.kind === "boundary" ? null : { kind: "boundary" },
                    )
                  }
                >
                  <Spline className="h-4 w-4" />{" "}
                  {draw?.kind === "boundary"
                    ? "Drawing…"
                    : hasBoundary
                      ? "Redraw"
                      : "Trace Boundary"}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    hasBoundary
                      ? setShowMapSettings(true)
                      : setShowQuickDims(true)
                  }
                >
                  <Ruler className="h-4 w-4" /> Dimensions
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowMapSettings(true)}
                >
                  <Ruler className="h-4 w-4" /> Map Settings
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowAddSection(true)}
                >
                  <Plus className="h-4 w-4" /> Add via form
                </Button>
                <div className="relative">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setFeatureMenuOpen((o) => !o)}
                  >
                    <Building2 className="h-4 w-4" /> Add Feature
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                  {featureMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setFeatureMenuOpen(false)}
                      />
                      <div className="absolute right-0 top-full z-30 mt-1 w-44 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-raised)] p-1 shadow-[var(--shadow-xl)]">
                        {FEATURE_MENU.map((f) => (
                          <button
                            key={f.kind}
                            onClick={() => {
                              setFeatureMenuOpen(false);
                              setDraw({
                                kind: "feature-new",
                                featureKind: f.kind,
                                geometry: FEATURE_GEOMETRY[f.kind],
                              });
                            }}
                            className="flex w-full items-center justify-between rounded-[var(--radius-sm)] px-2.5 py-1.5 text-left text-xs text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]"
                          >
                            {f.label}
                            <span className="text-[10px] text-[var(--text-tertiary)]">
                              {FEATURE_GEOMETRY[f.kind] === "polyline"
                                ? "line"
                                : "area"}
                            </span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() =>
                    setDraw((d) =>
                      d?.kind === "area-new" ? null : { kind: "area-new" },
                    )
                  }
                >
                  <PenTool className="h-4 w-4" />{" "}
                  {draw?.kind === "area-new" ? "Drawing…" : "Draw Area"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Draw-mode hint bar */}
      {draw && (
        <div className="flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-interactive-primary)]/30 bg-[var(--color-interactive-primary)]/5 px-3 py-2 text-xs text-[var(--text-secondary)]">
          <span>
            {draw.kind === "boundary"
              ? "Click to place the facility's corner points. Click the first point again to close the boundary."
              : draw.kind === "area-reshape"
                ? "Re-trace this storage area's outline. Click the first point again to close it."
                : (draw.kind === "feature-new" ||
                      draw.kind === "feature-reshape") &&
                    draw.geometry === "polyline"
                  ? "Click to place points along the line. Double-click or press Enter to finish."
                  : draw.kind === "feature-new"
                    ? "Trace the feature's outline. Click the first point again to close it."
                    : "Trace the storage area's outline. Click the first point again to close it."}
          </span>
          <button
            onClick={() => setDraw(null)}
            className="font-medium text-[var(--color-interactive-primary)] hover:underline"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Clearance violations banner */}
      {mode === "layout" && violations.length > 0 && (
        <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 px-3 py-2 text-xs text-[var(--text-secondary)]">
          <AlertTriangle className="h-4 w-4 shrink-0 text-[var(--color-warning)]" />
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            <span className="font-medium text-[var(--text-primary)]">
              {violations.length} clearance{" "}
              {violations.length === 1 ? "issue" : "issues"}:
            </span>
            {violations.slice(0, 4).map((v, i) => (
              <button
                key={`${v.id}-${i}`}
                onClick={() => selectSection(v.id)}
                className="hover:underline"
              >
                {v.label} {v.issue}
                {i < Math.min(violations.length, 4) - 1 ? "," : ""}
              </button>
            ))}
            {violations.length > 4 && (
              <span>and {violations.length - 4} more</span>
            )}
          </div>
        </div>
      )}

      {/* Add Storage Area via form — creates an unplaced area to draw/drag in */}
      <ActionDrawer
        isOpen={showAddSection}
        onClose={() => setShowAddSection(false)}
        title="New Storage Area"
        description="Create a storage area, then place it on the map by drawing its outline or dragging it from the palette."
        icon={Plus}
        size="xl"
      >
        {businessLocationId ? (
          <SectionSetupWizard
            businessLocationId={businessLocationId}
            onCreated={() => setShowAddSection(false)}
            onCancel={() => setShowAddSection(false)}
          />
        ) : (
          <SectionSetupWizard
            yardId={yardId}
            existingSectionCount={sections.length}
            onCreated={() => setShowAddSection(false)}
            onCancel={() => setShowAddSection(false)}
          />
        )}
      </ActionDrawer>

      {/* Map Settings Drawer (dimensions + unit) */}
      <MapSettingsDrawer
        isOpen={showMapSettings}
        onClose={() => setShowMapSettings(false)}
        initialWidth={yard.width}
        initialHeight={yard.height}
        initialUnit={unit}
        onSave={(data) =>
          updateYardMutation.mutate(data, {
            onSuccess: () => {
              toast.success("Map settings saved");
              setShowMapSettings(false);
            },
          })
        }
        saving={updateYardMutation.isPending}
      />

      {/* Quick-dimensions dialog — the rectangle path during boundary setup */}
      <MapSettingsDrawer
        isOpen={showQuickDims}
        onClose={() => setShowQuickDims(false)}
        title="Facility dimensions"
        description="Enter the overall size of your facility. This creates a rectangular boundary and sets the scale — you can re-trace a custom outline anytime."
        initialWidth={yard.width}
        initialHeight={yard.height}
        initialUnit={unit}
        onSave={({ width, height, unit: u }) =>
          updateYardMutation.mutate(
            {
              width,
              height,
              unit: u,
              boundary_points: rectBoundary(width, height),
            },
            {
              onSuccess: () => {
                toast.success("Facility boundary set");
                setShowQuickDims(false);
                setEditorMode("layout");
              },
            },
          )
        }
        saving={updateYardMutation.isPending}
      />

      {/* Draw-then-classify dialog for a freshly traced area outline */}
      {pendingShape && (
        <ClassifyDialog
          unplacedAreas={unplacedAreas}
          canCreate={!!businessLocationId}
          onAssign={classifyExisting}
          onCreate={classifyNew}
          onCancel={() => setPendingShape(null)}
        />
      )}

      {/* Keyboard-shortcuts help */}
      {showShortcuts && (
        <ShortcutsOverlay onClose={() => setShowShortcuts(false)} />
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <CanvasContextMenu
          target={contextMenu}
          locked={lockedIds.has(contextMenu.id)}
          onDuplicate={() => {
            if (contextMenu.type === "section")
              duplicateSection(contextMenu.id);
            else duplicateFeature(contextMenu.id);
            setContextMenu(null);
          }}
          onRotate={
            contextMenu.type === "section"
              ? () => {
                  rotateSection(contextMenu.id, 90);
                  setContextMenu(null);
                }
              : undefined
          }
          onToggleLock={() => {
            toggleLock(contextMenu.id);
            setContextMenu(null);
          }}
          onDelete={() => {
            const { id, type } = contextMenu;
            setContextMenu(null);
            if (type === "section") {
              const s = sections.find((x) => x.id === id);
              const n = s?.asset_count || 0;
              if (
                confirm(
                  `Delete "${s?.label ?? "area"}"?${n > 0 ? ` ${n} asset(s) will be un-assigned.` : ""}`,
                )
              ) {
                deleteSectionMutation.mutate(id);
              }
            } else {
              deleteFeatureMutation.mutate(id, {
                onSuccess: () => {
                  if (selectedFeature === id) setSelectedFeature(null);
                  toast.success("Feature deleted");
                },
              });
            }
          }}
          onClose={() => setContextMenu(null)}
        />
      )}

      <div className="flex gap-4 flex-col lg:flex-row">
        {/* SVG Map Canvas */}
        <div className="relative flex-1" ref={canvasColRef}>
          <YardCanvas
            yardId={yardId}
            yardWidth={yard.width}
            yardHeight={yard.height}
            unit={unit}
            boundaryPoints={boundaryPoints}
            setbackPoints={setbackPoints}
            sections={sections}
            selectedSectionId={selectedSection}
            onSelectSection={selectSection}
            features={visibleFeatures}
            selectedFeatureId={selectedFeature}
            onSelectFeature={selectFeature}
            schemes={schemes ?? []}
            fitScale={scale}
            drawMode={draw !== null}
            drawGeometry={
              draw &&
              (draw.kind === "feature-new" || draw.kind === "feature-reshape")
                ? draw.geometry
                : "polygon"
            }
            onBoundaryComplete={handleDrawComplete}
            onCancelDraw={() => setDraw(null)}
            onDropArea={mode === "layout" ? handleDropArea : undefined}
            onCommitGeometry={commitSectionGeometry}
            lockedIds={lockedIds}
            onShapeContextMenu={setContextMenu}
          />

          {/* Boundary-first onboarding overlay */}
          {!hasBoundary && draw?.kind !== "boundary" && (
            <BoundarySetupOverlay
              onTrace={() => setDraw({ kind: "boundary" })}
              onEnterDimensions={() => setShowQuickDims(true)}
            />
          )}
        </div>

        {/* Section Detail Panel */}
        <div className="w-full lg:w-80 shrink-0 space-y-3">
          {selectedData ? (
            <>
              <Card>
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="h-4 w-4 rounded"
                    style={{ backgroundColor: selectedData.color }}
                  />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                    {selectedData.label}
                  </h3>
                  <Badge variant="info">{selectedData.section_type}</Badge>
                  {hasScheme && selectedData.location_scheme_id && (
                    <Badge variant="default">
                      <Grid3X3 className="h-2.5 w-2.5 mr-0.5" />
                      {schemes?.find(
                        (s) => s.id === selectedData.location_scheme_id,
                      )?.name ?? "Scheme"}{" "}
                      ({selectedData.location_count ?? 0})
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-[var(--text-tertiary)]">Assets</p>
                    <p className="font-semibold text-[var(--text-primary)]">
                      {selectedData.asset_count || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-[var(--text-tertiary)]">Capacity</p>
                    <p className="font-semibold text-[var(--text-primary)]">
                      {selectedData.capacity ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[var(--text-tertiary)]">Area</p>
                    <p className="font-semibold text-[var(--text-primary)]">
                      {Math.round(
                        polygonArea(sectionPolygon(selectedData)),
                      ).toLocaleString()}{" "}
                      {unit}²
                    </p>
                  </div>
                </div>

                {/* Assign asset */}
                <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                  <label className="block text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-1.5">
                    Add Asset to Storage Area
                  </label>
                  <div className="space-y-2">
                    {hasScheme && selectedLocations.length > 0 && (
                      <select
                        value={assignLocationId}
                        onChange={(e) => setAssignLocationId(e.target.value)}
                        className="block w-full h-8 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-glass)] px-2 text-xs"
                      >
                        <option value="">Select location first…</option>
                        {selectedLocations.map((loc) => (
                          <option
                            key={loc.id}
                            value={loc.id}
                            disabled={(loc.asset_count || 0) >= loc.capacity}
                          >
                            {loc.label} ({loc.asset_count || 0}/{loc.capacity})
                          </option>
                        ))}
                      </select>
                    )}
                    <AssetPicker
                      excludeAssetIds={selectedAssets.map((a) => a.id)}
                      disabled={hasScheme && !assignLocationId}
                      onSelect={(assetId) => {
                        apiClient
                          .post("/api/v1/yards/assign-asset", {
                            asset_id: assetId,
                            yard_section_id: selectedSection,
                            yard_location_id: assignLocationId || undefined,
                          })
                          .then(() => {
                            qc.invalidateQueries({
                              queryKey: ["yard-map", yardId],
                            });
                            setAssignLocationId("");
                            toast.success("Asset assigned");
                          })
                          .catch(() => toast.error("Failed to assign asset"));
                      }}
                      placeholder={
                        hasScheme && !assignLocationId
                          ? "Select a location first"
                          : "Search by SKU, name, serial..."
                      }
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-3 border-t border-[var(--border-subtle)] pt-2">
                  <button
                    onClick={() => rotateSection(selectedData.id, 90)}
                    className="flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
                    title="Rotate 90°"
                  >
                    <RotateCw className="h-3 w-3" /> Rotate
                  </button>
                  <button
                    onClick={() => duplicateSection(selectedData.id)}
                    className="flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
                    title="Duplicate (⌘D)"
                  >
                    <Copy className="h-3 w-3" /> Duplicate
                  </button>
                  <button
                    onClick={() => toggleLock(selectedData.id)}
                    className="flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
                    title={lockedIds.has(selectedData.id) ? "Unlock" : "Lock"}
                  >
                    {lockedIds.has(selectedData.id) ? "Unlock" : "Lock"}
                  </button>
                </div>

                <div className="mt-2 flex items-center justify-between gap-3">
                  <button
                    onClick={() =>
                      setDraw({
                        kind: "area-reshape",
                        sectionId: selectedData.id,
                      })
                    }
                    className="text-xs text-[var(--color-interactive-primary)] hover:underline flex items-center gap-1"
                    title="Trace a custom outline of connected line segments for this storage area"
                  >
                    <PenTool className="h-3 w-3" /> Draw shape
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            "Remove this storage area from the map? The area, its locations, and asset assignments will be preserved.",
                          )
                        ) {
                          unplaceMutation.mutate(selectedData.id);
                        }
                      }}
                      className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline flex items-center gap-1"
                    >
                      <EyeOff className="h-3 w-3" /> Unplace
                    </button>
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            `Delete "${selectedData.label}"?${(selectedData.asset_count || 0) > 0 ? ` ${selectedData.asset_count} asset(s) will be un-assigned.` : ""}`,
                          )
                        ) {
                          deleteSectionMutation.mutate(selectedData.id);
                        }
                      }}
                      className="text-xs text-[var(--text-danger)] hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </Card>

              {/* Clearance (distances, warnings, exact gap to boundary) */}
              {selectedClearance &&
                (hasBoundary || closedShapes.length > 1) && (
                  <ClearanceCard
                    info={selectedClearance}
                    unit={unit}
                    hasBoundary={hasBoundary}
                    onSetGap={nudgeSelectedToBoundary}
                  />
                )}

              {/* Location Browser (when section has a scheme) */}
              {hasScheme && selectedLocations.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[var(--text-tertiary)]" />
                      <input
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        placeholder="Filter locations…"
                        className="w-full h-7 pl-7 pr-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-glass)] text-xs placeholder:text-[var(--text-tertiary)]"
                      />
                    </div>
                    <span className="text-[10px] text-[var(--text-tertiary)] shrink-0">
                      {filteredLocations.length}/{selectedLocations.length}
                    </span>
                  </div>

                  <div className="max-h-80 overflow-y-auto space-y-1">
                    {filteredLocations.map((loc) => {
                      const locFill =
                        loc.capacity > 0
                          ? ((loc.asset_count || 0) / loc.capacity) * 100
                          : 0;
                      const fillColor =
                        locFill >= 100
                          ? "bg-[var(--fill-danger)]"
                          : locFill >= 70
                            ? "bg-[var(--fill-warning)]"
                            : locFill > 0
                              ? "bg-[var(--fill-success)]"
                              : "bg-[var(--surface-sunken)]";
                      const locAssets = loc.assets ?? [];
                      return (
                        <Card key={loc.id} padding="sm">
                          <div className="flex items-center gap-2">
                            <div
                              className={`h-2.5 w-2.5 rounded-full shrink-0 ${fillColor}`}
                            />
                            <span className="text-xs font-mono font-semibold text-[var(--text-primary)] flex-1 truncate">
                              {loc.label}
                            </span>
                            <span className="text-[10px] text-[var(--text-tertiary)]">
                              {loc.asset_count || 0}/{loc.capacity}
                            </span>
                          </div>
                          {locAssets.length > 0 && (
                            <div className="mt-1.5 ml-4 space-y-1">
                              {locAssets.map((a) => (
                                <div
                                  key={a.id}
                                  className="flex items-center gap-2 text-[10px]"
                                >
                                  <span className="font-mono font-bold text-brand-600 dark:text-brand-300">
                                    {a.sku}
                                  </span>
                                  <span className="text-[var(--text-primary)] truncate flex-1">
                                    {a.name}
                                  </span>
                                  <Badge
                                    variant={
                                      a.status === "Received" ||
                                      a.status === "Available"
                                        ? "success"
                                        : a.status === "In Service"
                                          ? "warning"
                                          : "default"
                                    }
                                  >
                                    {a.status}
                                  </Badge>
                                  <button
                                    onClick={() =>
                                      unassignMutation.mutate(a.id)
                                    }
                                    className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)] shrink-0"
                                  >
                                    <Trash2 className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* Direct asset list (no scheme) */
                <div className="space-y-2">
                  {selectedAssets.map((a) => {
                    const ymm = [a.year, a.make, a.model]
                      .filter(Boolean)
                      .join(" ");
                    const cogs =
                      typeof a.total_cogs === "number" ? a.total_cogs : 0;
                    return (
                      <Card key={a.id} padding="sm" className="hover-lift">
                        <div className="flex gap-2.5">
                          {a.image ? (
                            <img
                              src={a.image}
                              alt={a.name}
                              className="h-12 w-12 rounded-md object-cover shrink-0"
                            />
                          ) : (
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[var(--surface-sunken)]">
                              <Move className="h-5 w-5 text-[var(--text-tertiary)]" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] font-mono font-bold text-brand-600 dark:text-brand-300">
                                {a.sku}
                              </span>
                              <Badge
                                variant={
                                  a.status === "Received" ||
                                  a.status === "Available"
                                    ? "success"
                                    : a.status === "In Service" ||
                                        a.status === "Reserved"
                                      ? "warning"
                                      : "default"
                                }
                              >
                                {a.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-[var(--text-primary)] truncate mt-0.5">
                              {a.name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[var(--text-tertiary)]">
                              <span>{a.category}</span>
                              {ymm && <span>· {ymm}</span>}
                              {cogs > 0 && (
                                <span>· ${cogs.toLocaleString()}</span>
                              )}
                            </div>
                          </div>

                          <button
                            onClick={() => unassignMutation.mutate(a.id)}
                            className="shrink-0 self-start mt-1 text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </Card>
                    );
                  })}
                  {selectedAssets.length === 0 && !hasScheme && (
                    <p className="text-xs text-[var(--text-tertiary)] text-center py-4">
                      No assets in this section
                    </p>
                  )}
                </div>
              )}
            </>
          ) : selectedFeatureData ? (
            <FeatureDetailPanel
              feature={selectedFeatureData}
              onRename={(label) =>
                updateFeatureMutation.mutate({
                  featureId: selectedFeatureData.id,
                  data: { label },
                })
              }
              onDuplicate={() => duplicateFeature(selectedFeatureData.id)}
              onRedraw={() =>
                setDraw({
                  kind: "feature-reshape",
                  featureId: selectedFeatureData.id,
                  geometry: selectedFeatureData.geometry_type,
                })
              }
              onDelete={() => {
                if (confirm(`Delete "${selectedFeatureData.label}"?`)) {
                  deleteFeatureMutation.mutate(selectedFeatureData.id, {
                    onSuccess: () => {
                      setSelectedFeature(null);
                      toast.success("Feature deleted");
                    },
                    onError: () => toast.error("Failed to delete feature"),
                  });
                }
              }}
            />
          ) : mode === "boundary" ? (
            <>
              {/* Boundary segment editor (when an outline exists) */}
              {hasBoundary && boundaryPoints && (
                <BoundaryEditor
                  points={boundaryPoints}
                  unit={unit}
                  setback={setback}
                  setbackValid={!!setbackPoints || !setback}
                  onSetback={(v) =>
                    updateYardMutation.mutate(
                      { setback: v },
                      {
                        onSuccess: () =>
                          toast.success(
                            v
                              ? `Setback set to ${v} ${unit}`
                              : "Setback removed",
                          ),
                      },
                    )
                  }
                  onSegmentLength={handleSegmentLength}
                  onClear={() =>
                    updateYardMutation.mutate(
                      { boundary_points: null, setback: null },
                      { onSuccess: () => toast.success("Boundary cleared") },
                    )
                  }
                />
              )}
              <Card>
                <div className="flex flex-col items-center py-8 text-center">
                  <MapPin className="h-8 w-8 text-[var(--text-tertiary)]" />
                  <p className="mt-2 text-sm font-medium text-[var(--text-primary)]">
                    {hasBoundary ? "Facility boundary" : "Define your facility"}
                  </p>
                  <p className="mt-1 text-xs text-[var(--text-tertiary)] max-w-[16rem]">
                    {hasBoundary
                      ? "Adjust segment lengths at left, or Redraw the outline. Switch to Layout to place storage areas inside."
                      : "Trace the outline of your facility or enter its dimensions. This sets the scale for everything placed inside."}
                  </p>
                </div>
              </Card>
            </>
          ) : (
            <>
              {/* Unplaced storage-area palette (drag onto the map) */}
              {businessLocationId && (
                <Card>
                  <div className="flex items-center gap-2 mb-2">
                    <PackageOpen className="h-4 w-4 text-[var(--text-tertiary)]" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                      Unplaced Storage Areas
                    </h3>
                  </div>
                  {unplacedAreas.length > 0 ? (
                    <>
                      <p className="text-[11px] text-[var(--text-tertiary)] mb-2">
                        Drag an area onto the map, or use{" "}
                        <span className="font-medium">Draw Area</span> to trace
                        it in place.
                      </p>
                      <div className="space-y-1.5">
                        {unplacedAreas.map((area) => (
                          <div
                            key={area.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData(AREA_DRAG_MIME, area.id);
                              e.dataTransfer.effectAllowed = "copy";
                            }}
                            className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-2.5 py-2 cursor-grab active:cursor-grabbing hover:border-[var(--border-strong)]"
                          >
                            <Move className="h-3.5 w-3.5 text-[var(--text-tertiary)] shrink-0" />
                            <span className="text-xs font-medium text-[var(--text-primary)] flex-1 truncate">
                              {area.label}
                            </span>
                            <Badge variant="default">{area.section_type}</Badge>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-[11px] text-[var(--text-tertiary)]">
                      No unplaced areas. Use{" "}
                      <span className="font-medium">Draw Area</span> to trace
                      one in place, or add one via form.
                    </p>
                  )}
                </Card>
              )}

              {/* Layer toggles for reference features */}
              {featureKindsPresent.length > 0 && (
                <Card>
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="h-4 w-4 text-[var(--text-tertiary)]" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                      Layers
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {featureKindsPresent.map((kind) => (
                      <label
                        key={kind}
                        className="flex items-center gap-2 text-xs text-[var(--text-primary)] cursor-pointer select-none capitalize"
                      >
                        <input
                          type="checkbox"
                          checked={!hiddenKinds.has(kind)}
                          onChange={() => toggleKind(kind)}
                          className="h-3.5 w-3.5 accent-[var(--color-interactive-primary)]"
                        />
                        {kind}
                        <span className="ml-auto text-[10px] text-[var(--text-tertiary)]">
                          {features.filter((f) => f.kind === kind).length}
                        </span>
                      </label>
                    ))}
                  </div>
                </Card>
              )}

              <Card>
                <div className="flex flex-col items-center py-8 text-center">
                  <MousePointerClick className="h-8 w-8 text-[var(--text-tertiary)]" />
                  <p className="mt-2 text-sm text-[var(--text-secondary)]">
                    Select a storage area or feature to view its details
                  </p>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Right-panel detail for a selected reference feature. */
function FeatureDetailPanel({
  feature,
  onRename,
  onDuplicate,
  onRedraw,
  onDelete,
}: {
  feature: YardFeature;
  onRename: (label: string) => void;
  onDuplicate: () => void;
  onRedraw: () => void;
  onDelete: () => void;
}) {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="h-4 w-4 text-[var(--text-tertiary)]" />
        <Badge variant="default">{feature.kind}</Badge>
        <span className="text-[10px] text-[var(--text-tertiary)]">
          {feature.geometry_type === "polyline" ? "line" : "area"}
        </span>
      </div>
      <label className="block text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-1">
        Label
      </label>
      <input
        defaultValue={feature.label}
        key={feature.id}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v && v !== feature.label) onRename(v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="block w-full h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
      />
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-[var(--border-subtle)] pt-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onRedraw}
            className="text-xs text-[var(--color-interactive-primary)] hover:underline flex items-center gap-1"
          >
            <PenTool className="h-3 w-3" /> Redraw
          </button>
          <button
            onClick={onDuplicate}
            className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline flex items-center gap-1"
          >
            <Copy className="h-3 w-3" /> Duplicate
          </button>
        </div>
        <button
          onClick={onDelete}
          className="text-xs text-[var(--text-danger)] hover:underline"
        >
          Delete
        </button>
      </div>
    </Card>
  );
}

/** Round a distance to a readable value (1 decimal under 100). */
function fmtDist(v: number, unit: string): string {
  const r = v >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
  return `${r} ${unit}`;
}

/** Right-panel clearance readouts + warnings + exact gap-to-boundary. */
function ClearanceCard({
  info,
  unit,
  hasBoundary,
  onSetGap,
}: {
  info: {
    boundaryGap: number | null;
    crossesBoundary: boolean;
    crossesSetback: boolean;
    nearest: { label: string; gap: number } | null;
    overlapsWith: string | null;
  };
  unit: string;
  hasBoundary: boolean;
  onSetGap: (target: number) => void;
}) {
  const [gap, setGap] = useState("");
  const warn = "text-[var(--color-warning)]";
  return (
    <Card>
      <div className="flex items-center gap-2 mb-2">
        <Ruler className="h-4 w-4 text-[var(--text-tertiary)]" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Clearance
        </h3>
      </div>
      <div className="space-y-1 text-xs">
        {hasBoundary && (
          <div className="flex items-center justify-between">
            <span className="text-[var(--text-tertiary)]">To boundary</span>
            {info.crossesBoundary ? (
              <span className={`font-medium ${warn}`}>crosses boundary</span>
            ) : (
              <span className="font-semibold text-[var(--text-primary)]">
                {info.boundaryGap != null
                  ? fmtDist(info.boundaryGap, unit)
                  : "—"}
              </span>
            )}
          </div>
        )}
        {info.crossesSetback && !info.crossesBoundary && (
          <div className="flex items-center justify-between">
            <span className="text-[var(--text-tertiary)]">Setback</span>
            <span className={`font-medium ${warn}`}>enters setback</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-[var(--text-tertiary)]">Nearest</span>
          {info.overlapsWith ? (
            <span className={`font-medium ${warn}`}>
              overlaps {info.overlapsWith}
            </span>
          ) : info.nearest ? (
            <span className="font-semibold text-[var(--text-primary)] truncate max-w-[10rem]">
              {info.nearest.label} · {fmtDist(info.nearest.gap, unit)}
            </span>
          ) : (
            <span className="text-[var(--text-tertiary)]">—</span>
          )}
        </div>
      </div>

      {hasBoundary && (
        <div className="mt-3 flex items-center gap-2 border-t border-[var(--border-subtle)] pt-3">
          <span className="text-[11px] text-[var(--text-tertiary)] shrink-0">
            Gap to boundary
          </span>
          <input
            type="number"
            min={0}
            value={gap}
            onChange={(e) => setGap(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && gap !== "") {
                onSetGap(Number(gap));
                setGap("");
              }
            }}
            placeholder="ft"
            className="h-7 w-16 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)]"
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={gap === ""}
            onClick={() => {
              onSetGap(Number(gap));
              setGap("");
            }}
          >
            Apply
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Drawer for editing facility-map dimensions and unit of measure. */
function MapSettingsDrawer({
  isOpen,
  onClose,
  initialWidth,
  initialHeight,
  initialUnit,
  onSave,
  saving,
  title = "Map Settings",
  description = "Set the overall size of the facility map. Drawn line lengths are measured in this unit.",
}: {
  isOpen: boolean;
  onClose: () => void;
  initialWidth: number;
  initialHeight: number;
  initialUnit: string;
  onSave: (data: { width: number; height: number; unit: string }) => void;
  saving: boolean;
  title?: string;
  description?: string;
}) {
  const [width, setWidth] = useState(String(initialWidth));
  const [height, setHeight] = useState(String(initialHeight));
  const [unit, setUnit] = useState(initialUnit);

  // Re-sync form fields whenever the drawer is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setWidth(String(initialWidth));
      setHeight(String(initialHeight));
      setUnit(initialUnit);
    }
  }, [isOpen, initialWidth, initialHeight, initialUnit]);

  const w = Number(width);
  const h = Number(height);
  const valid = w > 0 && h > 0;

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      icon={Ruler}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              Width
            </label>
            <input
              type="number"
              min={1}
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              className="block w-full h-9 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
              Height
            </label>
            <input
              type="number"
              min={1}
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              className="block w-full h-9 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
            Unit of measure
          </label>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="block w-full h-9 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)]"
          >
            <option value="ft">Feet (ft)</option>
            <option value="m">Meters (m)</option>
            <option value="yd">Yards (yd)</option>
          </select>
          <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
            Changing the unit relabels measurements; it does not rescale
            existing dimensions.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--border-subtle)] pt-4">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!valid}
            isLoading={saving}
            onClick={() => valid && onSave({ width: w, height: h, unit })}
          >
            Save
          </Button>
        </div>
      </div>
    </ActionDrawer>
  );
}

/** Right-panel editor listing each boundary segment with an editable length. */
function BoundaryEditor({
  points,
  unit,
  setback,
  setbackValid,
  onSetback,
  onSegmentLength,
  onClear,
}: {
  points: Point[];
  unit: string;
  setback: number | null;
  setbackValid: boolean;
  onSetback: (value: number | null) => void;
  onSegmentLength: (index: number, newLength: number) => void;
  onClear: () => void;
}) {
  const [setbackInput, setSetbackInput] = useState(
    setback != null ? String(setback) : "",
  );
  useEffect(() => {
    setSetbackInput(setback != null ? String(setback) : "");
  }, [setback]);

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Spline className="h-4 w-4 text-[var(--text-tertiary)]" />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            Boundary
          </h3>
        </div>
        <button
          onClick={onClear}
          className="text-[11px] text-[var(--text-danger)] hover:underline"
        >
          Clear
        </button>
      </div>

      {/* Facility area & perimeter */}
      <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-[var(--text-tertiary)]">Area</p>
          <p className="font-semibold text-[var(--text-primary)]">
            {Math.round(polygonArea(points)).toLocaleString()} {unit}²
          </p>
        </div>
        <div>
          <p className="text-[var(--text-tertiary)]">Perimeter</p>
          <p className="font-semibold text-[var(--text-primary)]">
            {Math.round(polygonPerimeter(points)).toLocaleString()} {unit}
          </p>
        </div>
      </div>

      {/* Setback (keep-clear inset) */}
      <div className="mb-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[var(--text-tertiary)] flex-1">
            Setback
          </span>
          <input
            type="number"
            min={0}
            value={setbackInput}
            onChange={(e) => setSetbackInput(e.target.value)}
            onBlur={() => {
              const v = setbackInput === "" ? null : Number(setbackInput);
              if (v !== setback) onSetback(v && v > 0 ? v : null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            placeholder="none"
            className="h-7 w-16 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)]"
          />
          <span className="text-[11px] text-[var(--text-tertiary)]">
            {unit}
          </span>
        </div>
        <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">
          {setback && !setbackValid
            ? "⚠ Setback too large for this outline — not shown."
            : "Dashed keep-clear line inset from the boundary."}
        </p>
      </div>
      <p className="text-[11px] text-[var(--text-tertiary)] mb-2">
        Edit a segment length to set it exactly ({unit}).
      </p>
      <div className="space-y-1.5">
        {points.map((a, i) => {
          const b = points[(i + 1) % points.length]!;
          const len = segmentLength(a, b);
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[11px] text-[var(--text-tertiary)] w-16 shrink-0">
                Segment {i + 1}
              </span>
              <input
                type="number"
                min={1}
                defaultValue={Math.round(len)}
                onBlur={(e) => {
                  const v = Number(e.target.value);
                  if (v > 0 && Math.round(v) !== Math.round(len)) {
                    onSegmentLength(i, v);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                className="h-7 w-20 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)]"
              />
              <span className="text-[11px] text-[var(--text-tertiary)]">
                {unit}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/** Header segmented control switching between the Boundary and Layout steps. */
function ModeStepper({
  mode,
  onChange,
}: {
  mode: "boundary" | "layout";
  onChange: (m: "boundary" | "layout") => void;
}) {
  const opts = [
    { key: "boundary" as const, n: "1", label: "Boundary", icon: MapPin },
    { key: "layout" as const, n: "2", label: "Layout", icon: LayoutTemplate },
  ];
  return (
    <div className="inline-flex items-center rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-0.5">
      {opts.map((o) => {
        const active = mode === o.key;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={`flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2.5 py-1 text-xs font-medium transition-colors ${
              active
                ? "bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-sm"
                : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
            }`}
          >
            <span
              className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${
                active
                  ? "bg-[var(--color-interactive-primary)] text-white"
                  : "bg-[var(--surface-raised)] text-[var(--text-tertiary)]"
              }`}
            >
              {o.n}
            </span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Onboarding overlay shown on a facility with no boundary yet. */
function BoundarySetupOverlay({
  onTrace,
  onEnterDimensions,
}: {
  onTrace: () => void;
  onEnterDimensions: () => void;
}) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[var(--radius-lg)] bg-[var(--surface-base)]/70 backdrop-blur-[1px]">
      <div className="max-w-sm rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-raised)] p-6 text-center shadow-[var(--shadow-xl)]">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-interactive-primary)]/10">
          <MapPin className="h-6 w-6 text-[var(--color-interactive-primary)]" />
        </div>
        <h3 className="mt-3 text-base font-semibold text-[var(--text-primary)]">
          Start with your facility boundary
        </h3>
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">
          Define the outer edge of your property first. This sets the scale and
          gives every storage area a place to live inside.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={onTrace}>
            <Spline className="h-4 w-4" /> Trace the outline
          </Button>
          <Button variant="secondary" onClick={onEnterDimensions}>
            <Ruler className="h-4 w-4" /> Enter dimensions
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Common storage-area types offered when creating one from a drawn outline. */
const QUICK_AREA_TYPES = [
  { value: "parking_lot", label: "Parking lot" },
  { value: "zone", label: "Zone" },
  { value: "pallet_rack", label: "Pallet rack" },
  { value: "shelving", label: "Shelving" },
  { value: "cold_storage", label: "Cold storage" },
  { value: "staging", label: "Staging" },
  { value: "dock", label: "Dock" },
  { value: "container", label: "Container" },
  { value: "other", label: "Other" },
];

/**
 * Draw-then-classify modal: after tracing an outline, decide which storage
 * area it represents — an existing unplaced one, or a brand-new area.
 */
function ClassifyDialog({
  unplacedAreas,
  canCreate,
  onAssign,
  onCreate,
  onCancel,
}: {
  unplacedAreas: StorageArea[];
  canCreate: boolean;
  onAssign: (areaId: string) => void;
  onCreate: (label: string, sectionType: string) => void;
  onCancel: () => void;
}) {
  const [tab, setTab] = useState<"pick" | "new">(
    unplacedAreas.length > 0 ? "pick" : "new",
  );
  const [label, setLabel] = useState("");
  const [type, setType] = useState("parking_lot");

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-raised)] p-5 shadow-[var(--shadow-xl)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              What did you draw?
            </h3>
            <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
              Assign this outline to a storage area.
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 inline-flex rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-0.5 text-xs">
          <button
            onClick={() => setTab("pick")}
            disabled={unplacedAreas.length === 0}
            className={`rounded-[var(--radius-sm)] px-3 py-1 font-medium disabled:opacity-40 ${
              tab === "pick"
                ? "bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-sm"
                : "text-[var(--text-tertiary)]"
            }`}
          >
            Existing area
          </button>
          <button
            onClick={() => setTab("new")}
            disabled={!canCreate}
            className={`rounded-[var(--radius-sm)] px-3 py-1 font-medium disabled:opacity-40 ${
              tab === "new"
                ? "bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-sm"
                : "text-[var(--text-tertiary)]"
            }`}
          >
            New area
          </button>
        </div>

        {tab === "pick" ? (
          <div className="mt-3 max-h-64 space-y-1.5 overflow-y-auto">
            {unplacedAreas.length > 0 ? (
              unplacedAreas.map((area) => (
                <button
                  key={area.id}
                  onClick={() => onAssign(area.id)}
                  className="flex w-full items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-3 py-2 text-left hover:border-[var(--border-strong)]"
                >
                  <PackageOpen className="h-4 w-4 text-[var(--text-tertiary)] shrink-0" />
                  <span className="flex-1 truncate text-sm font-medium text-[var(--text-primary)]">
                    {area.label}
                  </span>
                  <Badge variant="default">{area.section_type}</Badge>
                  <ArrowRight className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
                </button>
              ))
            ) : (
              <p className="py-4 text-center text-xs text-[var(--text-tertiary)]">
                No unplaced areas. Create a new one instead.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
                Name
              </label>
              <input
                autoFocus
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && label.trim())
                    onCreate(label.trim(), type);
                }}
                placeholder="e.g. North Lot, Bay 3"
                className="block h-9 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
                Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="block h-9 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)]"
              >
                {QUICK_AREA_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2 border-t border-[var(--border-subtle)] pt-3">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          {tab === "new" && (
            <Button
              size="sm"
              disabled={!label.trim()}
              onClick={() => label.trim() && onCreate(label.trim(), type)}
            >
              Create & place
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
